import { useQuery, UseQueryResult } from "@tanstack/react-query";
import saveAs from "file-saver";
import config from "../config.json";
import { IPageable, Option, Params } from "../types/table/interfaces.type";
import { createAxiosInstance } from "./ServicesHelpers";

const axiosInstance = createAxiosInstance(config.ADMIN_API_BASE_URL);

export interface RoutingPlanType {
  rate: number;
  lastRate: number;
  id: string;
  name: string;
  cloneOf: string;
  isDeleted: boolean;
  createdDate: string;
  createdBy: string;
  updatedBy?: string;
  currencyId?: number;
}
export interface IRoutingPlanHistory {
  id: string;
  snapshot: RoutingPlanType & { timestamp: string };
  historyTimestamp: string;
  mainId: number;
  createdBy: string;
}

export const exportRoutingPlan = async (
  params: Params<RoutingPlanType & { fromDate?: string; toDate?: string }>,
): Promise<void> => {
  try {
    const response = await axiosInstance.get("/routing-plans/csv", {
      params,
      responseType: "blob",
    });

    const disposition = response.headers["content-disposition"];
    let filename = "RoutingPlan.csv";
    if (disposition) {
      const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (match && match[1]) {
        filename = match[1].replace(/['"]/g, "").trim();
      }
    }

    const blob = new Blob([response.data], { type: "text/csv" });
    saveAs(blob, filename);

    console.log("Export successful and file downloaded.");
  } catch (error: any) {
    console.error(
      "Error exporting RoutingPlan:",
      error.response?.data?.message || error.message || error,
    );
  }
};

export async function fetchRoutingPlans(
  params: Params<RoutingPlanType>,
): Promise<IPageable<RoutingPlanType>> {
  try {
    const response = await axiosInstance.get("/routing-plans", { params });
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while getting the users.",
    );
  }
}

export async function fetchRoutingPlan(
  id: RoutingPlanType["id"],
): Promise<RoutingPlanType> {
  try {
    const response = await axiosInstance.get(`/routing-plans/${id}`);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        `An error occurred while getting routing-plan/${id}.`,
    );
  }
}

export const addRoutingPlanApi = async (data: Partial<RoutingPlanType>) => {
  try {
    const { cloneOf, ...body } = data;
    const response = await axiosInstance.post("/routing-plans", body, {
      params: { cloneOf },
    });
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while adding the routing plan.",
    );
  }
};

export const editRoutingPlanApi = async (
  id: string,
  data: Partial<RoutingPlanType>,
) => {
  try {
    const { cloneOf, ...body } = data;

    const response = await axiosInstance.put(`/routing-plans/${id}`, body, {
      params: { cloneOf },
    });
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while updating the routing plan.",
    );
  }
};

export const getRoutingPlanById = async (id: string) => {
  try {
    const response = await axiosInstance.get(`/routing-plans/${id}`);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while fetching the routing plan.",
    );
  }
};

export const deleteRoutingPlansApi = async (ids: string[]): Promise<void> => {
  try {
    await axiosInstance.delete("/routing-plans", { data: ids });
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while deleting multiple routing plans.",
    );
  }
};

export const bulkUpdateCurrencyApi = async (
  ids: string[],
  currencyId: number,
): Promise<RoutingPlanType[]> => {
  try {
    const response = await axiosInstance.put("/routing-plans/bulk/currency", {
      ids,
      currencyId,
    });
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while updating currencies for routing plans.",
    );
  }
};

export const useRoutingPlanQuery = (
  params: Params<RoutingPlanType>,
): UseQueryResult<IPageable<RoutingPlanType>, Error> => {
  const effectiveParams = {
    ...params,
    sort: params.sort || "createdDate,desc",
  };
  const queryKey = ["routingPlan", JSON.stringify(effectiveParams)];
  return useQuery({
    queryKey,
    queryFn: () => fetchRoutingPlans(effectiveParams),
  });
};

export const useRoutingPlansQuery = (): UseQueryResult<Option[], Error> => {
  const queryKey = [
    "routingPlans",
    JSON.stringify({ sort: "name", size: 10000 }),
  ];
  return useQuery({
    queryKey,
    queryFn: async () => {
      const routingPlans = await fetchRoutingPlans({
        sort: "name",
        size: 10000,
      });
      const routingPlanOptions: Option[] = routingPlans.content.map(
        (plan: any) => ({
          label: plan.name,
          value: plan.id,
        }),
      );
      return routingPlanOptions;
    },
  });
};

export const getRoutingPlanName = (routingPlanId: string) => ({
  queryKey: ["routingPlan", routingPlanId],
  queryFn: async (): Promise<string> => {
    const routingPlan = await fetchRoutingPlan(routingPlanId);
    return routingPlan?.name;
  },
  enabled: Boolean(routingPlanId) && routingPlanId !== "undefined",
  select: (name: string) => name,
});

export async function fetchIRoutingPlanHistory(
  id: RoutingPlanType["id"],
  params: Params<RoutingPlanType> = {},
): Promise<IPageable<RoutingPlanType>> {
  try {
    const response = await axiosInstance.get(`/routing-plans/history`, {
      params: { mainId: id, sort: "historyTimestamp,desc", ...params },
    });
    const data = response.data;
    return {
      ...data,
      content: data.content.map((item: any) => ({
        ...item.snapshot,
        historyTimestamp: item.historyTimestamp,
      })),
    };
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        `An error occurred while getting the FallBack Routing history for ID ${id}.`,
    );
  }
}
